/**
 * Proposal Snippets — Pre-fill templates for proposal text fields
 *
 * Two categories (hospitality, community-living), each with audience sub-types.
 * When a user selects an audience, the intro_paragraph and other text fields
 * are pre-filled with tailored copy. Users can always override with free text.
 */

export interface ProposalSnippet {
  label: string;
  intro: string;
  ambassador_quote?: string;
}

export interface ProposalCategory {
  label: string;
  audiences: Record<string, ProposalSnippet>;
}

export const PROPOSAL_CATEGORIES: Record<string, ProposalCategory> = {
  hospitality: {
    label: 'Hospitality',
    audiences: {
      'boutique-hotel': {
        label: 'Boutique Hotel',
        intro: 'Your guests chose your property for its character and attention to detail. Peace of Sleep certification adds an invisible layer of care — one that discerning guests feel even if they never see the certificate.',
        ambassador_quote: 'If you have any questions about this proposal or about Peace of Sleep™, I\'m here to help. I look forward to hearing from you.',
      },
      'safari-lodge': {
        label: 'Safari Lodge',
        intro: 'In the bush, the experience is everything. Guests return from a day in the wild expecting a sanctuary. A certified sleep environment completes that promise — no chemicals, no disruption, just a clean surface they can trust.',
        ambassador_quote: 'If you have any questions about this proposal or about Peace of Sleep™, I\'m here to help. I look forward to hearing from you.',
      },
      'resort-spa': {
        label: 'Resort / Spa',
        intro: 'Wellness is your brand promise. Peace of Sleep certification extends that promise to the one place guests spend the most time — their bed. A verified, chemical-free standard that aligns with everything your resort stands for.',
        ambassador_quote: 'If you have any questions about this proposal or about Peace of Sleep™, I\'m here to help. I look forward to hearing from you.',
      },
      'guest-house': {
        label: 'Guest House / B&B',
        intro: 'In a guest house, the personal touch matters. Your guests trust you with their comfort. Peace of Sleep certification gives you something tangible to stand behind — a verified hygiene standard that sets you apart from the listings.',
        ambassador_quote: 'If you have any questions about this proposal or about Peace of Sleep™, I\'m here to help. I look forward to hearing from you.',
      },
      'serviced-apartments': {
        label: 'Serviced Apartments',
        intro: 'Long-stay guests treat your apartments as home. Over weeks and months, mattress hygiene becomes a real concern — not just for comfort, but for health. Peace of Sleep certification gives your residents confidence that it\'s managed.',
        ambassador_quote: 'If you have any questions about this proposal or about Peace of Sleep™, I\'m here to help. I look forward to hearing from you.',
      },
      'airbnb': {
        label: 'Airbnb / Short-term Rental',
        intro: 'Reviews drive bookings. A single comment about mattress hygiene can undo months of 5-star ratings. Peace of Sleep certification gives you a verified standard you can display in your listing — and a response to the reviewer who asks.',
        ambassador_quote: 'If you have any questions about this proposal or about Peace of Sleep™, I\'m here to help. I look forward to hearing from you.',
      },
      'hotel': {
        label: 'Hotel',
        intro: 'Peace of Sleep™ certifies that your property\'s mattresses have been professionally sanitised, verified, and documented. A quiet standard your property can stand behind, and your guests can trust.',
        ambassador_quote: 'If you have any questions about this proposal or about Peace of Sleep™, I\'m here to help. I look forward to hearing from you.',
      },
    },
  },
  'community-living': {
    label: 'Community Living',
    audiences: {
      'junior-school': {
        label: 'Junior School',
        intro: 'When parents entrust their youngest children to boarding, they need to know the basics are covered. A professionally sanitised mattress is a simple, powerful reassurance that your school takes every aspect of care seriously.',
        ambassador_quote: 'I understand how important your students\' wellbeing is. If you have any questions about this proposal or how Peace of Sleep™ works within a school environment, I\'m here to help.',
      },
      'senior-school': {
        label: 'Senior School',
        intro: 'Boarding students spend a third of their school life on their mattress. For parents making significant investment in their child\'s education, knowing that sleep hygiene is formally managed and certified provides quiet confidence.',
        ambassador_quote: 'I understand how important your students\' wellbeing is. If you have any questions about this proposal or how Peace of Sleep™ works within a school environment, I\'m here to help.',
      },
      'university': {
        label: 'University / Residence',
        intro: 'Student residences cycle hundreds of occupants through the same mattresses year after year. Peace of Sleep certification gives your institution a documented standard that protects both students and your reputation.',
        ambassador_quote: 'If you have any questions about this proposal or how Peace of Sleep™ works within a residential environment, I\'m here to help.',
      },
      'sports-academy': {
        label: 'Sports Academy',
        intro: 'Sleep is the most underrated performance tool in any athlete\'s programme. A contaminated mattress can trigger inflammatory responses that compromise recovery, reaction time, and injury resilience. Your academy can remove that variable entirely.',
        ambassador_quote: 'I\'d welcome the chance to discuss how Peace of Sleep™ supports athletic performance through better sleep hygiene. Please don\'t hesitate to reach out.',
      },
      'military': {
        label: 'Military / Barracks',
        intro: 'Personnel readiness depends on recovery. A verified, chemical-free mattress sanitisation programme supports operational fitness while demonstrating duty of care for those in your command.',
        ambassador_quote: 'If you have any questions about this proposal or how Peace of Sleep™ works within a military or institutional environment, I\'m here to help.',
      },
      'worker-accommodation': {
        label: 'Worker Accommodation',
        intro: 'Your workforce deserves a clean sleep environment. Peace of Sleep certification demonstrates your commitment to employee wellbeing and gives you a documented standard you can evidence to stakeholders.',
        ambassador_quote: 'If you have any questions about this proposal or how Peace of Sleep™ supports workforce wellbeing, I\'m here to help.',
      },
      'retirement-community': {
        label: 'Retirement Community',
        intro: 'Your residents have chosen your community for the quality of life it offers. A clean, verified sleep environment is a fundamental part of that promise. Peace of Sleep certification gives families confidence that wellbeing extends to the details that matter most.',
        ambassador_quote: 'If you have any questions about this proposal or how Peace of Sleep™ supports resident wellbeing, I\'m here to help.',
      },
    },
  },
};

/** Get audiences for a category */
export function getAudiences(category: string): { value: string; label: string }[] {
  const cat = PROPOSAL_CATEGORIES[category];
  if (!cat) return [];
  return Object.entries(cat.audiences).map(([value, { label }]) => ({ value, label }));
}

/** Get snippet for a category + audience */
export function getSnippet(category: string, audience: string): ProposalSnippet | null {
  return PROPOSAL_CATEGORIES[category]?.audiences[audience] ?? null;
}

/** Get all category options */
export function getCategoryOptions(): { value: string; label: string }[] {
  return Object.entries(PROPOSAL_CATEGORIES).map(([value, { label }]) => ({ value, label }));
}
